import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/AuthContext";
import { useLocale } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";
import { ASSETS } from "@/lib/api";

export default function Login({ mode = "login" }) {
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const { login, signup } = useAuth();
  const { t } = useTranslation();
  const { lang } = useLocale();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || localisedTo("/", lang);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        await signup(form.email, form.password, form.name);
        toast.success(t("auth.welcomeToast"));
      } else {
        await login(form.email, form.password);
        toast.success(t("auth.signedInToast"));
      }
      navigate(next);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("auth.failed"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden bg-black">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(239,43,43,0.15),transparent_55%)]" />

      <div className="w-full max-w-md relative">
        <Link
          to={localisedTo("/", lang)}
          data-testid="auth-back-home"
          className="font-mono text-[10px] tracking-[0.3em] text-white/50 hover:text-white transition-colors inline-flex items-center gap-2"
        >
          {t("auth.backToSite")}
        </Link>

        <div className="mt-6 hairline rounded-2xl bg-white/[0.02] p-10">
          <img src={ASSETS.logo} alt="DJ SPY" className="h-12 w-auto" />
          <div className="mt-6">
            <div className="font-mono text-[10px] tracking-[0.3em] text-red-brand">
              {mode === "signup" ? t("auth.kickerSignup") : t("auth.kickerLogin")}
            </div>
            <div className="font-anton text-3xl text-white leading-none mt-2">
              {mode === "signup" ? t("auth.titleSignup") : t("auth.titleLogin")}
            </div>
          </div>

          <form className="mt-8 space-y-4" onSubmit={onSubmit} data-testid={`${mode}-form`}>
            {mode === "signup" && (
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] text-white/50">{t("auth.name")}</label>
                <input data-testid="auth-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("auth.namePlaceholder")} />
              </div>
            )}
            <div>
              <label className="font-mono text-[10px] tracking-[0.3em] text-white/50">{t("auth.email")}</label>
              <input data-testid="auth-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("auth.emailPlaceholder")} dir="ltr" />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.3em] text-white/50">{t("auth.password")}</label>
              <input data-testid="auth-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={t("auth.passwordPlaceholder")} dir="ltr" />
            </div>
            <button type="submit" disabled={loading} data-testid="auth-submit" className="btn-primary w-full justify-center">
              {loading ? <Loader2 size={18} className="animate-spin" /> : null}
              {loading ? t("auth.pleaseWait") : mode === "signup" ? t("auth.submitSignup") : t("auth.submitLogin")}
            </button>
            {mode === "login" && (
              <div className="text-center pt-1">
                <Link to={localisedTo("/forgot-password", lang)} data-testid="auth-forgot-password" className="font-mono text-[10px] tracking-[0.3em] text-white/50 hover:text-red-brand transition-colors">
                  FORGOT PASSWORD?
                </Link>
              </div>
            )}
          </form>

          <div className="mt-6 text-center text-white/50 text-sm">
            {mode === "signup" ? (
              <>{t("auth.haveAccount")} <Link to={localisedTo("/login", lang)} data-testid="auth-switch" className="text-red-brand hover:underline">{t("auth.switchToLogin")}</Link></>
            ) : (
              <>{t("auth.newHere")} <Link to={localisedTo("/signup", lang)} data-testid="auth-switch" className="text-red-brand hover:underline">{t("auth.switchToSignup")}</Link></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
